// Zones tab (F-STN-19; spec/13 §3.4, §12): the named areas of a venue's floor — Main floor, VIP room,
// Bootcamp — grouped by level, each a card with its colour. "New zone" and Edit open a dialog. A zone's
// rectangle on the plan is drawn in arrange mode (the drag-and-drop surface is a follow-up).
//
// Zones are defined on the floor plan, so creating and editing one needs `floor:arrange` (spec/13 §12);
// `floor:read` sees them read-only. A zone's floor is fixed once created — a zone that belongs on
// another level is a new zone (spec/13 §3.4).

import type { ColorToken, CreateZone, UpdateZone, Zone } from '@clavian/api-client';
import { FLOOR_MAX, FLOOR_MIN, MAX_ZONES_PER_FLOOR } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, EmptyState, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { CardsSkeleton, Notice, Toolbar, ToolbarSpring } from '../ui';
import { ColorTokenField, tokenColor } from './fields';
import { useCreateZone, useUpdateZone, useZones } from './queries';

export function ZonesTab({
  organizationId,
  venueId,
  canArrange,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly canArrange: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const zones = useZones(organizationId, venueId);
  const [editing, setEditing] = useState<Zone | 'new' | null>(null);

  const byFloor = useMemo(() => {
    const groups = new Map<number, Zone[]>();
    for (const zone of zones.data ?? []) {
      const list = groups.get(zone.floor) ?? [];
      list.push(zone);
      groups.set(zone.floor, list);
    }
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [zones.data]);

  let body: ReactNode;
  if (zones.data === undefined) {
    body = zones.isError ? (
      <LoadFailure error={zones.error} message={t('stations.zones.unavailable')} onRetry={() => void zones.refetch()} />
    ) : (
      <CardsSkeleton count={3} height="5rem" />
    );
  } else if (zones.data.length === 0) {
    body = (
      <EmptyState
        icon={<Icon name="pin" />}
        title={t('stations.zones.emptyTitle')}
        description={t('stations.zones.empty')}
        action={
          canArrange ? (
            <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setEditing('new')}>
              {t('stations.zones.new')}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = byFloor.map(([floor, floorZones]) => (
      <section key={floor} className="console-zone-floor" aria-labelledby={`console-zone-floor-${floor}`}>
        <h3 id={`console-zone-floor-${floor}`} className="console-zone-floor__title">
          {floorLabel(t, floor)}
          <span className="console-subtle console-num">{t('stations.zones.count', { count: floorZones.length })}</span>
        </h3>
        <ul className="console-zone-cards">
          {floorZones.map((zone) => (
            <li key={zone.id}>
              <article className="console-zone-card" style={{ '--tile-class': tokenColor(zone.colorToken) } as CSSProperties}>
                <span className="console-zone-card__swatch" aria-hidden="true" />
                <h4 className="console-zone-card__name">{zone.name}</h4>
                {canArrange ? (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(zone)} label={t('stations.zones.editNamed', { name: zone.name })}>
                    <span>{t('stations.zones.edit')}</span>
                  </Button>
                ) : null}
              </article>
            </li>
          ))}
        </ul>
      </section>
    ));
  }

  return (
    <div className="console-tabpane">
      <Toolbar>
        <p className="console-muted console-tabpane__intro">{t('stations.zones.intro')}</p>
        <ToolbarSpring />
        {canArrange && zones.data !== undefined && zones.data.length > 0 ? (
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setEditing('new')}>
            {t('stations.zones.new')}
          </Button>
        ) : null}
      </Toolbar>
      {body}
      {/* A zone is a short form, so it opens in a centred framed dialog — the console has no slide-out panels
          (DESIGN.md "Modals, never slide-out panels"). */}
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        size="md"
        dirty="auto"
        className="console-glass"
        title={editing === 'new' || editing === null ? t('stations.zones.new') : t('stations.zones.editTitle')}
        description={t('stations.zones.drawerHint')}
      >
        {editing === null ? null : (
          <ZoneForm
            key={editing === 'new' ? 'new' : editing.id}
            organizationId={organizationId}
            venueId={venueId}
            editing={editing === 'new' ? undefined : editing}
            onDone={() => setEditing(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

function floorLabel(t: ReturnType<typeof useTranslation>['t'], floor: number): string {
  return floor === 0 ? t('stations.zones.floorGround') : t('stations.zones.floorN', { floor });
}

interface ZoneFormState {
  readonly name: string;
  readonly colorToken: ColorToken;
  readonly floor: string;
}

function formFor(editing: Zone | undefined): ZoneFormState {
  // A new zone starts on a categorical hue (#411/ADR-0025); the picker offers only these.
  if (editing === undefined) return { name: '', colorToken: 'category-2', floor: '0' };
  return { name: editing.name, colorToken: editing.colorToken, floor: String(editing.floor) };
}

/** The New/Edit zone form. `editing` undefined creates on a chosen floor; a zone edits its name and colour. */
function ZoneForm({
  organizationId,
  venueId,
  editing,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly editing: Zone | undefined;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const create = useCreateZone(organizationId, venueId);
  const update = useUpdateZone(organizationId, venueId);
  const [form, setForm] = useState<ZoneFormState>(() => formFor(editing));
  const [errors, setErrors] = useState<{ name?: string; form?: string }>({});
  const busy = create.isPending || update.isPending;

  const floorOptions = Array.from({ length: FLOOR_MAX - FLOOR_MIN + 1 }, (_unused, index) => {
    const floor = FLOOR_MIN + index;
    return { value: String(floor), label: floorLabel(t, floor) };
  });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (form.name.trim() === '') {
      setErrors({ name: t('stations.zones.error.nameRequired') });
      return;
    }
    setErrors({});
    const onError = (error: unknown): void => {
      const nameMessage = problem.fieldMessage(error, 'name');
      if (problemCode(error) === PROBLEM.conflict) setErrors({ form: t('stations.zones.error.conflict', { max: MAX_ZONES_PER_FLOOR }) });
      else if (nameMessage !== undefined) setErrors({ name: nameMessage });
      else setErrors({ form: problem.text(error, { fallback: t('stations.zones.error.refused') }) });
    };
    const onSuccess = () => {
      show({ tone: 'ok', title: t('stations.zones.saved', { name: form.name.trim() }) });
      onDone();
    };
    if (editing === undefined) {
      const body: CreateZone = { name: form.name.trim(), colorToken: form.colorToken, floor: Number(form.floor) };
      create.mutate(body, { onSuccess, onError });
    } else {
      const body: UpdateZone = { name: form.name.trim(), colorToken: form.colorToken };
      update.mutate({ zoneId: editing.id, body }, { onSuccess, onError });
    }
  }

  return (
    <form className="console-form" aria-label={t('stations.zones.formLabel')} noValidate onSubmit={submit}>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      <TextField label={t('stations.zones.field.name')} required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} error={errors.name} />
      <ColorTokenField label={t('stations.zones.field.color')} value={form.colorToken} onValueChange={(colorToken) => setForm((current) => ({ ...current, colorToken }))} />
      {editing === undefined ? (
        <SelectField
          label={t('stations.zones.field.floor')}
          description={t('stations.zones.floorHint')}
          options={floorOptions}
          value={form.floor}
          onValueChange={(value) => setForm((current) => ({ ...current, floor: value }))}
        />
      ) : (
        <Notice role="note">{t('stations.zones.floorFixed', { floor: floorLabel(t, editing.floor) })}</Notice>
      )}
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('stations.zones.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={busy}>
          {editing === undefined ? t('stations.zones.create') : t('stations.zones.save')}
        </Button>
      </div>
    </form>
  );
}
